// Helpers for the Press Coverage CMS page — no networking side effects
// live in the components themselves so the search / hash / revalidate
// call sites stay small and swappable.
//
// `searchRss` talks to nextjsprojv1's /api/rss-search (staff-only — sends
// the signed-in user's Firebase ID token; previously a shared-secret
// proxy in front of the publisher feeds). The route is read-only; the
// actual add-to-feed writes happen client-side via Firestore's client
// SDK under the authenticated marketing user (rules require auth).

import { revalidateFor, staffAuthHeaders } from "./revalidatePublic";

const RSS_SEARCH_URL = process.env.REACT_APP_RSS_SEARCH_URL;

/**
 * Fetch fresh matches from the publisher RSS proxy. Returns the JSON
 * envelope the endpoint sends: { ok, query, count, items, errors }.
 * Throws on transport or auth failure; the UI catches and surfaces
 * the message.
 */
export async function searchRss({ query } = {}) {
  if (!RSS_SEARCH_URL) {
    throw new Error(
      "Press Coverage search is not configured — set REACT_APP_RSS_SEARCH_URL.",
    );
  }
  const res = await fetch(RSS_SEARCH_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(await staffAuthHeaders()),
    },
    body: JSON.stringify(query ? { query } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) {
    throw new Error(data?.error || `HTTP ${res.status}`);
  }
  return data;
}

/**
 * SHA-1 hex of a URL. Matches the server-side `sha1Hex` in
 * nextjsprojv1/src/app/utils/rss/sha1.js so the same URL always maps
 * to the same Firestore doc id on both sides.
 *
 * Uses the browser SubtleCrypto — no imports, no bundle size hit.
 */
export async function hashUrl(url) {
  const buf = new TextEncoder().encode(String(url));
  const digest = await crypto.subtle.digest("SHA-1", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Fire-and-forget revalidate of the public /news page. Called after
 * every add / hide / show / delete so the CDN picks up the change
 * within the next request rather than waiting on the 1h ISR window.
 * Delegates to the shared helper so all CMS writes go through one code
 * path (fire-and-forget, layout-type invalidation).
 */
export function revalidateNews() {
  revalidateFor("newsRss");
}
